import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { ELECTRONIC_SIGNATURE_CONSENT_LABEL } from '../../utilities/electronicSignatureConsent'
import { sanitizeAgreementHtml } from '../../utilities/sanitizeHtml'
import {
  EXPIRED_LINK_MESSAGE,
  INVALID_LINK_MESSAGE,
  PAGE_STATE
} from './constants'
import AgreementDownloadLink from './AgreementDownloadLink'
import { formatSignedDateTime } from './utils'
import styles from './index.module.scss'

export default function CustomerAgreementView ({
  pageState,
  agreement,
  signerName,
  setSignerName,
  accepted,
  setAccepted,
  formError,
  isSigning,
  canSign,
  handleSubmit
}) {
  const title = agreement?.title || 'Terms and Conditions'
  const safeContent = useMemo(
    () => sanitizeAgreementHtml(agreement?.content || ''),
    [agreement?.content]
  )

  if (pageState === PAGE_STATE.INVALID) {
    return <p className={styles.statusMessage}>{INVALID_LINK_MESSAGE}</p>
  }

  if (pageState === PAGE_STATE.LINK_EXPIRED) {
    return <p className={styles.statusMessage}>{EXPIRED_LINK_MESSAGE}</p>
  }

  if (pageState === PAGE_STATE.SIGNED) {
    const details = [
      { label: 'Customer', value: agreement?.signerName || signerName },
      { label: 'Signed', value: formatSignedDateTime(agreement?.signedAt) }
    ].filter((row) => row.value)

    return (
      <div className={styles.signedCard}>
        <div className={styles.successIconWrap} aria-hidden='true'>
          <i className='material-icons'>check</i>
        </div>
        <h2 className={styles.signedTitle}>Signature received</h2>
        <p className={styles.signedMessage}>
          Thanks — your terms & conditions have been signed and saved.
        </p>
        {details.length > 0 && (
          <dl className={styles.signedDetails}>
            {details.map((row) => (
              <div key={row.label} className={styles.signedDetailRow}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <AgreementDownloadLink downloadUrl={agreement?.downloadUrl} />
      </div>
    )
  }

  if (pageState === PAGE_STATE.UNSIGNED) {
    return (
      <>
        <h2 className={styles.docTitle}>{title}</h2>
        <div
          className={cn(styles.content, 'dis-mk-wrapper')}
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
        <form className={styles.signatureSection} onSubmit={handleSubmit}>
          <div className={styles.fullNameRow}>
            <label className={styles.fullNameLabel} htmlFor='agreement-signer-name'>
              Full Name
            </label>
            <input
              id='agreement-signer-name'
              type='text'
              className={styles.fullNameInput}
              value={signerName}
              onChange={(event) => setSignerName(event.target.value)}
              disabled={isSigning}
              autoComplete='name'
              aria-invalid={Boolean(formError)}
              aria-describedby={formError ? 'agreement-form-error' : undefined}
            />
          </div>
          <div className={styles.agreeRow}>
            <input
              id='agreement-accepted'
              type='checkbox'
              className={styles.agreeCheckbox}
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              disabled={isSigning}
            />
            <label className={styles.agreeLabel} htmlFor='agreement-accepted'>
              {ELECTRONIC_SIGNATURE_CONSENT_LABEL}
            </label>
          </div>
          {formError && (
            <p id='agreement-form-error' className={styles.formError} role='alert'>{formError}</p>
          )}
          <button
            type='submit'
            className={cn(styles.submitBtn, 'dis-btn dis-btn-primary')}
            disabled={!canSign}>
            {isSigning ? 'SIGNING...' : 'SUBMIT'}
            <i className='material-icons dis-btn-icon'>check</i>
          </button>
        </form>
      </>
    )
  }

  return null
}

CustomerAgreementView.propTypes = {
  pageState: PropTypes.string.isRequired,
  agreement: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    signerName: PropTypes.string,
    signedAt: PropTypes.string,
    downloadUrl: PropTypes.string
  }),
  signerName: PropTypes.string.isRequired,
  setSignerName: PropTypes.func.isRequired,
  accepted: PropTypes.bool.isRequired,
  setAccepted: PropTypes.func.isRequired,
  formError: PropTypes.string,
  isSigning: PropTypes.bool.isRequired,
  canSign: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired
}

CustomerAgreementView.defaultProps = {
  agreement: null,
  formError: ''
}
